import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { InquiryProvider } from "@/lib/inquiry-store";
import { DRAFT_PREVIEW } from "@/lib/concepts";
import { Toaster } from "sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-extrabold text-primary">404</h1>
        <h2 className="mt-4 font-display text-xl font-semibold">Sidan finns inte</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sidan du letar efter finns inte längre eller har flyttats.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:bg-accent/90"
          >
            Tillbaka till startsidan
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-xl font-semibold">Något gick fel</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Försök igen eller gå tillbaka till startsidan.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90"
          >
            Försök igen
          </button>
          <a
            href="/"
            className="rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold hover:bg-secondary"
          >
            Till startsidan
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Event Sverige — Företagsevent, mässor & koncept" },
      {
        name: "description",
        content:
          "Eventkoncept för företag och mässor. Aktiviteter, värd och stämningsljus skapar en plats att mötas på. Be om en samlad offert.",
      },
      { name: "author", content: "Event Sverige" },
      { name: "theme-color", content: "#0c2340" },
      { property: "og:site_name", content: "Event Sverige" },
      { property: "og:title", content: "Event Sverige — Företagsevent, mässor & koncept" },
      {
        property: "og:description",
        content:
          "Eventkoncept för företag och mässor. Aktiviteter, värd och stämningsljus skapar en plats att mötas på. Be om en samlad offert.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "sv_SE" },
      { property: "og:url", content: "https://eventsverige.se" },
      { property: "og:image", content: "https://eventsverige.se/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Event Sverige — Företagsevent, mässor & koncept" },
      {
        name: "twitter:description",
        content:
          "Eventkoncept för företag och mässor. Aktiviteter, värd och stämningsljus skapar en plats att mötas på. Be om en samlad offert.",
      },
      { name: "twitter:image", content: "https://eventsverige.se/og-image.jpg" },
      { name: "google-site-verification", content: "pqdt1W3Rhdo3C-Dfwcx_adGnHTRgs6ISqfmFUVWYNcI" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Figtree:wght@400;500;600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Figtree:wght@400;500;600;700&display=swap",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Event Sverige",
          url: "https://eventsverige.se",
          logo: "https://eventsverige.se/apple-touch-icon.png",
          email: "info@eventsverige.se",
          telephone: "+46317975599",
          areaServed: "SE",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="sv">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <InquiryProvider>
        <div className="flex min-h-screen flex-col">
          {DRAFT_PREVIEW && (
            <div className="concept-preview-note">
              Förhandsvisning – godkända paketpriser; innehåll och samlad release granskas.
            </div>
          )}
          <Header />
          <main className="flex-1">
            <Outlet />
          </main>
          <Footer />
        </div>
        <Toaster richColors position="top-center" />
      </InquiryProvider>
    </QueryClientProvider>
  );
}
